import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// Centre roughly on India so the map opens on the user base's region.
const INDIA_CENTER = [22.9734, 78.6569];
const INDIA_ZOOM = 5;

const dotIcon = L.divIcon({
  className: '',
  html: '<span style="display:block;width:12px;height:12px;border-radius:9999px;background:#9333EA;border:2px solid #ffffff;box-shadow:0 0 0 1px rgba(0,0,0,0.25)"></span>',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
  popupAnchor: [0, -6],
});

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
));

/**
 * Clustered map of user locations. Zoomed out shows grouped counts; zooming in
 * splits clusters down to individual user dots. Uses vanilla Leaflet driven
 * imperatively so it stays independent of react-leaflet's React version peer
 * requirements.
 */
const AdminDemographicsMap = ({ points = [] }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);

  // Initialise the map once.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return undefined;

    const map = L.map(containerRef.current, {
      center: INDIA_CENTER,
      zoom: INDIA_ZOOM,
      scrollWheelZoom: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);

    const cluster = L.markerClusterGroup({ chunkedLoading: true });
    map.addLayer(cluster);

    mapRef.current = map;
    clusterRef.current = cluster;

    return () => {
      map.remove();
      mapRef.current = null;
      clusterRef.current = null;
    };
  }, []);

  // Re-render markers whenever the points change.
  useEffect(() => {
    const cluster = clusterRef.current;
    const map = mapRef.current;
    if (!cluster || !map) return;

    cluster.clearLayers();
    const markers = [];
    points.forEach((point) => {
      const lat = Number(point.lat);
      const lng = Number(point.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const label = point.username || point.name || 'User';
      const place = [point.city, point.state].filter(Boolean).join(', ') || 'Unknown location';
      const marker = L.marker([lat, lng], { icon: dotIcon });
      marker.bindPopup(`<strong>${escapeHtml(label)}</strong><br/>${escapeHtml(place)}`);
      markers.push(marker);
    });
    if (markers.length) {
      cluster.addLayers(markers);
      // Fit to the located users, but don't zoom in absurdly for a single point.
      map.fitBounds(cluster.getBounds().pad(0.2), { maxZoom: 8 });
    } else {
      map.setView(INDIA_CENTER, INDIA_ZOOM);
    }
  }, [points]);

  // Cap the map in its own low stacking context. Leaflet gives its panes and
  // controls high z-indexes (400–1000 by default), which can paint over
  // modals/popups rendered later in the DOM. `isolation: isolate` plus
  // `position: relative; zIndex: 0` traps all of those internal z-indexes
  // inside this wrapper so the whole map always stays below any modal/popup layer.
  return (
    <div
      ref={containerRef}
      className="w-full rounded-[12px] overflow-hidden border border-[#ececec]"
      style={{ height: 360, position: 'relative', zIndex: 0, isolation: 'isolate' }}
      data-testid="admin-demographics-map"
    />
  );
};

export default AdminDemographicsMap;
